import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationController from '../Controllers/NotificationController';
import { toastNotification } from '../Utils/toast';

const POLL_MS = 60000;
const LAST_SEEN_KEY = 'ivs-last-seen-notification-id';

const SEVERITY_ICON = {
  info: 'bi-info-circle',
  warning: 'bi-exclamation-triangle',
  critical: 'bi-exclamation-octagon',
};

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const lastSeenIdRef = useRef(
    Number(localStorage.getItem(LAST_SEEN_KEY)) || null
  );

  const checkForNew = useCallback(async () => {
    try {
      const data = await NotificationController.list({ pageSize: 5 });
      const latest = data.data || [];
      if (!latest.length) return;

      if (lastSeenIdRef.current === null) {
        lastSeenIdRef.current = latest[0].id;
        localStorage.setItem(LAST_SEEN_KEY, String(latest[0].id));
        return;
      }

      const fresh = latest.filter((n) => n.id > lastSeenIdRef.current);
      if (fresh.length) {
        fresh
          .slice()
          .reverse()
          .forEach((n) => toastNotification(n.title, () => navigate('/notifications')));
        lastSeenIdRef.current = Math.max(...latest.map((n) => n.id));
        localStorage.setItem(LAST_SEEN_KEY, String(lastSeenIdRef.current));
      }
    } catch (err) {
      console.error('Failed to check for new notifications', err);
    }
  }, [navigate]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await NotificationController.unreadCount();
      setUnreadCount(data.count || 0);
    } catch (err) {
      console.error('Failed to load unread notification count', err);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    checkForNew();
    const interval = setInterval(() => {
      fetchUnreadCount();
      checkForNew();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, checkForNew]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const data = await NotificationController.list({ pageSize: 5 });
        setItems(data.data || []);
      } catch (err) {
        console.error('Failed to load notifications', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleItemClick = async (item) => {
    if (!item.read_at) {
      try {
        await NotificationController.markRead(item.id);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification read', err);
      }
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <div className="ivs-notif" ref={wrapperRef}>
      <button type="button" className="ivs-bell" aria-label="Notifications" onClick={openPanel}>
        <i className="bi bi-bell"></i>
        {unreadCount > 0 && <span className="ivs-bell__dot"></span>}
      </button>

      {open && (
        <div className="ivs-notif__panel">
          <div className="ivs-notif__header">
            <span>Notifications</span>
            {unreadCount > 0 && <span className="ivs-notif__badge">{unreadCount} unread</span>}
          </div>

          <div className="ivs-notif__list">
            {loading ? (
              <div className="ivs-empty-state">Loading…</div>
            ) : items.length === 0 ? (
              <div className="ivs-empty-state">You're all caught up</div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`ivs-notif__item${!item.read_at ? ' is-unread' : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <i className={`bi ${SEVERITY_ICON[item.severity] || 'bi-info-circle'} ivs-notif__icon is-${item.severity}`}></i>
                  <div className="ivs-notif__body">
                    <div className="ivs-notif__title">{item.title}</div>
                    {item.message && <div className="ivs-notif__message">{item.message}</div>}
                    <div className="ivs-notif__time">{timeAgo(item.created_at)}</div>
                  </div>
                </button>
              ))
            )}
          </div>

          <button type="button" className="ivs-notif__footer" onClick={handleViewAll}>
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
